import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Grid,
  useTheme,
  Paper,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { StyledContainer } from '../../dynamicform/styled.js';
import Title from '../shared/Title.jsx';
import TopBanner from '../shared/TopBanner.jsx';
import { useSelector, useDispatch } from 'react-redux';
import { userTypeSelector, darkModeSelector, securityGroupSelector } from '../../redux/selectors';
import { setDarkMode } from '../../redux/actions/app';
import { siteMiantenanceItems } from '../Settings/SettingsItems.jsx';
import AuthenticatorSetup from '../AuthenticatorSetup';
import UserDetails from './UserDetails';
import PasswordChange from './PasswordChange';
import ContrastIcon from '@mui/icons-material/Contrast';
import FaqTab from './FaqTab.jsx';
import EarlyAccessTab from './EarlyAccessTab.jsx';
import PortalAccessTab from './PortalAccessTab.jsx';

const TabPanel = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index} aria-labelledby={`admin-tab-${index}`}>
      {value === index && (
        <Box sx={{ pt: 2, backgroundColor: 'background.paper', minHeight: 'calc(100vh - 200px)' }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const Settings = () => {
  const [tab, setTab] = useState(0);
  const theme = useTheme();
  const [ActiveComponent, setActiveComponent] = useState(null);
  const currentUserType = useSelector(userTypeSelector);
  const darkMode = useSelector(darkModeSelector);
  const dispatch = useDispatch();
  const hasSettingsPerm =
    currentUserType.perms?.settings && currentUserType.perms.settings !== 'internal';
  const currentSelectedSecurityGroup = useSelector(securityGroupSelector);

  const isAdmin = !hasSettingsPerm && currentSelectedSecurityGroup === 'ADMIN';

  // Tab indices depend on which tabs are visible
  // ADMIN:    [0: Site Maint, 1: FAQ, 2: Beta Access, 3: Portal Access, 4: Preferences]
  // other internal: [0: Preferences]
  // external: [0: User Details, 1: Security, 2: Preferences]
  const portalAccessTabIndex = 3;
  const prefTabIndex = hasSettingsPerm ? 2 : isAdmin ? 4 : 0;

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        paddingTop: theme.spacing(2),
        paddingRight: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        marginLeft: '72px',
        gap: '16px',
        overflow: 'hidden',
      }}
    >
      <TopBanner bannerVariant="dashboard" />

      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 4,
          overflowY: 'auto',
          overflowX: 'hidden',
          height: 'calc(100vh - 55px)',
          minWidth: 'calc(100vw - 72px - 32px)',
          backgroundColor: 'background.default',
        }}
        elevation={4}
        variant="outlined"
      >
        <Title title="Settings"></Title>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {!hasSettingsPerm && currentSelectedSecurityGroup === 'ADMIN' && (
            <Tab label="SITE MAINTENANCE" id="tab-0" />
          )}
          {!hasSettingsPerm && currentSelectedSecurityGroup === 'ADMIN' && (
            <Tab label="FAQ EDITOR" id="tab-1" />
          )}
          {!hasSettingsPerm && currentSelectedSecurityGroup === 'ADMIN' && (
            <Tab label="BETA ACCESS" id="tab-early-access" />
          )}
          {isAdmin && <Tab label="PORTAL ACCESS" id="tab-portal-access" />}
          {hasSettingsPerm && <Tab label="USER DETAILS" id="tab-user-details" />}
          {hasSettingsPerm && <Tab label="SECURITY" id="tab-security" />}
          <Tab label="PREFERENCES" id="tab-preferences" />
        </Tabs>

        {/* Internal: Site Maintenance */}
        {isAdmin && (
          <TabPanel value={tab} index={0}>
            <Grid
              container
              spacing={5}
              sx={{ paddingLeft: theme.spacing(3), paddingRight: theme.spacing(3) }}
            >
              {siteMiantenanceItems.map(({ Icon, label, component }) => (
                <Grid size={{ xs: 12, md: 4 }} key={label}>
                  <StyledContainer
                    style={{
                      padding: theme.spacing(3),
                      display: 'flex',
                      alignItems: 'center',
                      cursor: component ? 'pointer' : 'default',
                    }}
                    onClick={() => {
                      component && setActiveComponent(() => component);
                    }}
                  >
                    <Box sx={{ mr: 3, display: 'flex', alignItems: 'center' }}>
                      <Icon color="tertiary" />
                    </Box>
                    <Box>
                      <Typography variant="overline">{label}</Typography>
                    </Box>
                  </StyledContainer>
                </Grid>
              ))}
            </Grid>
          </TabPanel>
        )}

        {/* Internal: FAQ Editor */}
        {isAdmin && (
          <TabPanel value={tab} index={1}>
            <FaqTab page="settings" />
          </TabPanel>
        )}

        {/* Internal: Beta Access */}
        {isAdmin && (
          <TabPanel value={tab} index={2}>
            <EarlyAccessTab />
          </TabPanel>
        )}

        {/* Internal: Portal Access (Admin only) */}
        {isAdmin && (
          <TabPanel value={tab} index={portalAccessTabIndex}>
            <PortalAccessTab />
          </TabPanel>
        )}

        {/* External: User Details */}
        {hasSettingsPerm && (
          <TabPanel value={tab} index={0}>
            <UserDetails />
          </TabPanel>
        )}

        {/* External: Security (Password Change + MFA side by side) */}
        {hasSettingsPerm && (
          <TabPanel value={tab} index={1}>
            <Grid container spacing={5} sx={{ padding: theme.spacing(3) }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <StyledContainer
                  style={{ padding: theme.spacing(3), height: '100%', boxSizing: 'border-box' }}
                >
                  <PasswordChange />
                </StyledContainer>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <StyledContainer
                  style={{ padding: theme.spacing(3), height: '100%', boxSizing: 'border-box' }}
                >
                  <AuthenticatorSetup />
                </StyledContainer>
              </Grid>
            </Grid>
          </TabPanel>
        )}

        {/* Preferences (both internal and external) */}
        <TabPanel value={tab} index={prefTabIndex}>
          <Grid container spacing={5} sx={{ padding: theme.spacing(3) }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <StyledContainer style={{ padding: theme.spacing(3) }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ContrastIcon color="tertiary" sx={{ mr: 1.5 }} />
                  <Typography variant="overline">Theme</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!darkMode}
                      onChange={() => dispatch(setDarkMode(!darkMode))}
                      color="primary"
                    />
                  }
                  label={darkMode ? 'Dark Mode' : 'Light Mode'}
                />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Toggle between light and dark theme for the application.
                </Typography>
              </StyledContainer>
            </Grid>
          </Grid>
        </TabPanel>
        {ActiveComponent && <ActiveComponent open onClose={() => setActiveComponent(null)} />}
      </Paper>
    </Box>
  );
};

export default Settings;
